import React from 'react';

export type IconButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonShape = 'square' | 'circle';

type BaseIconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'>;

export type IconButtonProps = BaseIconButtonProps & (
  | { label: string; 'aria-label'?: string }
  | { 'aria-label': string; label?: string }
) & {
  /** The icon node to render */
  icon: React.ReactNode;
  /** Visual style variant */
  variant?: IconButtonVariant;
  /** Control size: sm (32px), md (40px), lg (48px) with 44px minimum touch targets on mobile */
  size?: IconButtonSize;
  /** Shape: square (rounded-xl) or circle (rounded-full) */
  shape?: IconButtonShape;
  /** Shows an inline spinner replacing the icon and disables interaction */
  loading?: boolean;
  /** Optional href to render as an anchor link */
  href?: string;
  target?: string;
  rel?: string;
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      'aria-label': ariaLabelProp,
      icon,
      variant = 'ghost',
      size = 'md',
      shape = 'square',
      loading = false,
      disabled,
      className = '',
      type,
      href,
      target,
      rel,
      ...props
    },
    ref
  ) => {
    const accessibleLabel = label ?? ariaLabelProp ?? '';

    const sizeClasses: Record<IconButtonSize, string> = {
      sm: 'size-11 min-h-[44px] min-w-[44px] md:size-8 md:min-h-8 md:min-w-8 text-xs',
      md: 'size-11 min-h-[44px] min-w-[44px] md:size-10 md:min-h-10 md:min-w-10 text-sm',
      lg: 'size-12 min-h-[48px] min-w-[48px] text-base'
    };

    const variantClasses: Record<IconButtonVariant, string> = {
      primary:
        'bg-primary-fill text-white hover:bg-primary-focus focus-visible:ring-primary focus-ring',
      secondary:
        'bg-neutral border border-base-300 text-neutral-content hover:bg-base-300/80 hover:text-base-content focus-visible:ring-primary focus-ring',
      ghost:
        'bg-transparent text-base-content/80 hover:text-base-content hover:bg-base-300/60 focus-visible:ring-primary focus-ring',
      danger:
        'border border-error text-error-soft hover:bg-error hover:text-white focus-visible:ring-error'
    };

    const shapeClasses: Record<IconButtonShape, string> = {
      square: 'rounded-xl',
      circle: 'rounded-full'
    };

    const combinedClasses = `inline-flex items-center justify-center shrink-0 select-none transition-all duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-2 ring-offset-2 ring-offset-base-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none ${
      sizeClasses[size]
    } ${variantClasses[variant]} ${shapeClasses[shape]} ${className}`;

    const content = loading ? (
      <span
        className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
        aria-hidden="true"
      />
    ) : (
      icon
    );

    if (href) {
      return (
        <a
          ref={ref as unknown as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={rel}
          role="button"
          aria-label={accessibleLabel}
          title={props.title || accessibleLabel}
          aria-busy={loading}
          aria-disabled={disabled || loading}
          className={`${combinedClasses} ${
            disabled || loading ? 'pointer-events-none opacity-50 cursor-not-allowed' : ''
          }`}
          {...(props as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        ref={ref}
        type={type || 'button'}
        aria-label={accessibleLabel}
        title={props.title || accessibleLabel}
        disabled={disabled || loading}
        aria-busy={loading}
        className={combinedClasses}
        {...props}
      >
        {content}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';
